import React, { useCallback, useRef } from 'react';
import { Text, View, ScrollView, Pressable } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { StyleSheet } from 'react-native-unistyles';
import Clipboard from '@react-native-clipboard/clipboard';
import Toast from 'react-native-toast-message';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system';
import { captureRef } from 'react-native-view-shot';
import QRCode from 'react-native-qrcode-svg';
import SolidButton from '@/components/General/SolidButton';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import IconButton from '@/components/General/IconButton';
import { useRouter } from 'expo-router';
import usePlatform from '@/utils/hooks/usePlatform';

const APP_WEB_URL = (typeof __DEV__ !== 'undefined' && __DEV__)
    ? 'http://localhost:8081'
    : (process.env.EXPO_PUBLIC_APP_WEB_URL || 'https://app.reachchms.com');

/**
 * Share tab — shown when the form is public. Displays the share URL with a copy
 * button, a QR code for scanning, and publish/close/reopen lifecycle controls.
 */
const FormShareTab = ({ form, onPublish, onClose, onReopen, onArchive, onUnarchive, lifecycleSaving, canEdit }) => {
    const { theme: { colors, fonts, shadows } } = useUnistyles();
    const { isWeb } = usePlatform();
    const router = useRouter();
    const qrRef = useRef(null);

    const shareUrl = form.publicId ? `${APP_WEB_URL}/f/${form.publicId}` : null;

    const copyLink = useCallback(() => {
        if (!shareUrl) return;
        Clipboard.setString(shareUrl);
        Toast.show({ type: 'success', text2: 'Link copied to clipboard!' });
    }, [shareUrl]);

    const captureQR = useCallback(async () => {
        if (!qrRef.current) return null;
        try {
            // Wait two frames for layout/paint to commit
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            const base64 = await captureRef(qrRef, {
                format: 'png',
                quality: 1.0,
                result: 'base64',
            });
            return base64;
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to capture QR code' });
            return null;
        }
    }, []);

    const copyQRImage = useCallback(async () => {
        const base64 = await captureQR();
        if (!base64) return;

        try {
            if (isWeb) {
                // Web: convert base64 to blob and write to clipboard as image
                const blob = await (await fetch(`data:image/png;base64,${base64}`)).blob();
                if (navigator.clipboard && window.ClipboardItem) {
                    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                    Toast.show({ type: 'success', text2: 'QR code image copied to clipboard!' });
                } else {
                    // Fallback: copy the share URL as text
                    Clipboard.setString(shareUrl || '');
                    Toast.show({ type: 'info', text2: 'Image clipboard not supported — link copied instead' });
                }
            } else {
                // Native: use Clipboard.setImage with base64
                Clipboard.setImage(base64);
                Toast.show({ type: 'success', text2: 'QR code image copied to clipboard!' });
            }
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to copy QR code' });
        }
    }, [captureQR, isWeb, shareUrl]);

    const downloadQR = useCallback(async () => {
        const base64 = await captureQR();
        if (!base64) return;

        try {
            if (isWeb) {
                // Web: trigger browser download
                const link = document.createElement('a');
                link.href = `data:image/png;base64,${base64}`;
                link.download = `form-${form.publicId || 'qr'}-qr.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                Toast.show({ type: 'success', text2: 'QR code downloaded!' });
            } else {
                // Native: save to file system and share
                const fileUri = `${FileSystem.cacheDirectory}form-qr-${form.publicId || Date.now()}.png`;
                await FileSystem.writeAsStringAsync(fileUri, base64, {
                    encoding: FileSystem.EncodingType.Base64,
                });
                if (await Sharing.isAvailableAsync()) {
                    await Sharing.shareAsync(fileUri, {
                        mimeType: 'image/png',
                        dialogTitle: 'Save QR Code',
                    });
                } else {
                    Toast.show({ type: 'info', text2: 'Sharing not available on this device' });
                }
            }
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to download QR code' });
        }
    }, [captureQR, isWeb, form.publicId]);

    const status = form.status;

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            {!form.isPublic && (
                <Callout
                    type="info"
                    title="This form is not public"
                    message="Enable the Public Form toggle in Settings to generate a shareable link."
                />
            )}

            {form.isPublic && !shareUrl && (
                <Callout
                    type="warning"
                    title="Public link pending"
                    message="Save the form to generate a public share link."
                />
            )}

            {shareUrl && status === 'published' && (
                <SolidButton
                    text="View Form"
                    onPress={() => router.push(`/f/${form.publicId}`)}
                    type="primary"
                    iconName="open-outline"
                    iconBefore
                    testID="form-view-button"
                />
            )}

            {shareUrl && status === 'published' && (
                <View style={styles.shareCard(colors, shadows)}>
                    <Text style={styles.label(colors, fonts)}>Share Link</Text>
                    <View style={styles.urlRow(colors)}>
                        <Text style={styles.urlText(colors, fonts)} testID="form-share-url">{shareUrl}</Text>
                        <IconButton
                            iconName="copy-outline"
                            iconSize={18}
                            type="forms"
                            onPress={copyLink}
                            tooltipText="Copy link"
                            testID="form-copy-link"
                        />
                    </View>

                    <View style={styles.qrSection(colors)}>
                        <Text style={styles.label(colors, fonts)}>QR Code</Text>
                        <View
                            ref={qrRef}
                            style={styles.qrContainer()}
                            collapsable={false}
                        >
                            <QRCode
                                value={shareUrl}
                                size={200}
                                color="black"
                                backgroundColor="white"
                            />
                        </View>
                        <Text style={styles.qrHintText(colors)}>
                            Scan or open this link with a phone camera to share the form.
                        </Text>
                        <View style={styles.qrActions}>
                            <Pressable
                                onPress={copyQRImage}
                                style={({ hovered }) => styles.qrButton(colors, hovered)}
                                testID="form-copy-qr"
                            >
                                <Text style={styles.qrButtonText(colors, fonts)}>Copy QR Code Image</Text>
                            </Pressable>
                            <Pressable
                                onPress={downloadQR}
                                style={({ hovered }) => styles.qrButton(colors, hovered)}
                                testID="form-download-qr"
                            >
                                <Text style={styles.qrButtonText(colors, fonts)}>Download QR Code</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            )}

            <View style={styles.lifecycleCard(colors, shadows)}>
                <View style={styles.statusHeaderRow}>
                    <Text style={[styles.label(colors, fonts), { marginBottom: 0 }]}>Form Status</Text>
                    <Chip
                        colorVariant={status === 'published' ? 'success' : status === 'closed' ? 'warning' : 'textSecondary'}
                    >
                        {status}
                    </Chip>
                </View>

                {canEdit && (
                    <View style={styles.lifecycleActions}>
                        {status === 'draft' && (
                            <SolidButton
                                text="Publish Form"
                                onPress={onPublish}
                                processing={lifecycleSaving}
                                type="primary"
                                iconName="rocket-outline"
                                iconBefore
                                testID="form-publish-button"
                            />
                        )}
                        {status === 'published' && (
                            <SolidButton
                                text="Close Form"
                                onPress={onClose}
                                processing={lifecycleSaving}
                                type="error"
                                iconName="lock-closed-outline"
                                iconBefore
                                testID="form-close-button"
                            />
                        )}
                        {status === 'closed' && (
                            <SolidButton
                                text="Reopen Form"
                                onPress={onReopen}
                                processing={lifecycleSaving}
                                type="primary"
                                variant="outlined"
                                testID="form-reopen-button"
                            />
                        )}
                        {status === 'archived' && onUnarchive && (
                            <SolidButton
                                text="Unarchive Form"
                                onPress={onUnarchive}
                                processing={lifecycleSaving}
                                type="primary"
                                variant="outlined"
                                iconName="archive-outline"
                                iconBefore
                                testID="form-unarchive-button"
                            />
                        )}
                        {status !== 'archived' && onArchive && (
                            <SolidButton
                                text="Archive Form"
                                onPress={onArchive}
                                processing={lifecycleSaving}
                                type="textSecondary"
                                variant="outlined"
                                iconName="archive-outline"
                                iconBefore
                                testID="form-archive-button"
                            />
                        )}
                    </View>
                )}

                {status === 'draft' && (
                    <Text style={styles.helpText(colors)}>
                        Draft forms can&apos;t accept submissions. Publish to start collecting responses.
                    </Text>
                )}
                {status === 'closed' && (
                    <Text style={styles.helpText(colors)}>
                        Closed forms show &quot;no longer accepting responses&quot; to anyone opening the link.
                    </Text>
                )}
                {status === 'archived' && (
                    <Text style={styles.helpText(colors)}>
                        Archived forms are hidden from members and can&apos;t accept responses. Unarchive restores the form as a draft — it does not republish.
                    </Text>
                )}
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create((_theme) => ({
    container: { flex: 1 },
    content: {
        padding: 16,
        maxWidth: 600,
        alignSelf: 'center',
        width: '100%',
        paddingBottom: 100,
        gap: 12,
    },
    shareCard: (colors, shadows) => ({
        backgroundColor: colors.surface,
        borderRadius: 14,
        padding: 16,
        boxShadow: shadows.card,
    }),
    label: (colors, fonts) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    }),
    urlRow: (colors) => ({
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.surfaceVariant,
        borderRadius: 8,
        padding: 12,
        gap: 8,
    }),
    urlText: (colors, fonts) => ({
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.forms,
        flex: 1,
    }),
    qrSection: (colors) => ({
        alignItems: 'center',
        gap: 8,
        paddingTop: 16,
        marginTop: 8,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    }),
    qrContainer: () => ({
        backgroundColor: 'white',
        padding: 16,
        borderRadius: 12,
        marginTop: 4,
    }),
    qrHintText: (colors) => ({
        fontSize: 13,
        color: colors.textSecondary,
        textAlign: 'center',
    }),
    qrActions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        marginTop: 4,
    },
    qrButton: (colors, hovered) => ({
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: hovered ? colors.forms : colors.border,
        backgroundColor: hovered ? colors.surfaceVariant : colors.surface,
    }),
    qrButtonText: (colors, fonts) => ({
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.forms,
    }),

    lifecycleCard: (colors, shadows) => ({
        backgroundColor: colors.surface,
        borderRadius: 14,
        padding: 16,
        boxShadow: shadows.card,
    }),
    statusHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
    lifecycleActions: { gap: 8 },
    helpText: (colors) => ({
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 10,
    }),
}));

export default FormShareTab;
